/**
 * Skins (user-selectable UI themes, docs/themes/README.md): Velora's own
 * look plus classic looks familiar to Dentrix, Eaglesoft and Open Dental
 * users, so staff need no retraining.
 *
 * This file holds what every skin shares: the ids, the token-set shape and
 * the CSS builder. Each skin's colour, type, size and density values live
 * in its own file next to this one (`./dentrix.ts`, ...), owned by that
 * skin's agent, because raw colour values may only live in this package
 * (ADR-0007). The web app imports them lazily, so a skin nobody selected
 * costs nothing (no stylesheet, no bytes beyond a code-split chunk).
 *
 * `data-skin` is the attribute the tokens key on, set next to the existing
 * `data-theme` (dark/light), `data-density` and `data-clin`.
 */
import type { Density } from '../scale.js';

export const SKIN_IDS = ['velora', 'dentrix', 'eaglesoft', 'opendental'] as const;
export type SkinId = (typeof SKIN_IDS)[number];

/** Velora's own look is the default for every user (Dr. John's brief). */
export const DEFAULT_SKIN: SkinId = 'velora';

/** The attribute the skin tokens key on (on <html>, or a preview wrapper). */
export const SKIN_ATTR = 'data-skin';

export function isSkinId(value: unknown): value is SkinId {
  return typeof value === 'string' && (SKIN_IDS as readonly string[]).includes(value);
}

/**
 * Custom property name (without the leading `--`) to value. Names are
 * either tokens from theme.css or engine tokens from
 * packages/ui/src/themes/engine.css; a unit test rejects anything else, so
 * a typo fails CI instead of silently doing nothing.
 */
export type TokenMap = Readonly<Record<string, string>>;

/**
 * A mode's own colour roles (requests-dentrix I-1, requests-opendental R1,
 * requests-eaglesoft T2): names outside theme.css and engine.css that only
 * that mode's components read (`--dtx-navy-mid`, `--od-grid-title-bg`,
 * `--es-closed`). Every name must start with the mode's prefix
 * (`SKIN_ROLE_PREFIX`); the tokens test checks it. They are emitted in the
 * same blocks as the mode's tokens, so light, dark, density and previews
 * resolve them without a second stylesheet.
 */
export interface SkinRoleTokens {
  readonly base?: TokenMap;
  readonly light?: TokenMap;
  readonly dark?: TokenMap;
}

/** The prefix a mode's role names must start with. */
export const SKIN_ROLE_PREFIX: Readonly<Record<SkinId, string>> = {
  velora: 'vel-',
  dentrix: 'dtx-',
  eaglesoft: 'es-',
  opendental: 'od-',
};

export interface SkinTokens {
  /** The mode's own colour roles (prefixed names); see `SkinRoleTokens`. */
  readonly roles?: SkinRoleTokens;
  /** Applied in both modes: fonts, sizes, radii, row heights, chrome metrics. */
  readonly base?: TokenMap;
  /** Applied when `data-theme="light"` (colours, shadows). */
  readonly light?: TokenMap;
  /** Applied when `data-theme="dark"`. A light-only skin leaves this out. */
  readonly dark?: TokenMap;
  /** Per-density overrides (row height, control height, font size). */
  readonly density?: Readonly<Partial<Record<Density, TokenMap>>>;
  /**
   * Applied on the modern frame's side navigation and top bar in both modes
   * (`.vel-shell__nav`, `.vel-shell__top`), for a mode whose chrome keeps one
   * look while the content follows light or dark.
   */
  readonly chrome?: TokenMap;
}

/** Identity helper so a skin file gets type checking without a cast. */
export function defineSkinTokens(tokens: SkinTokens): SkinTokens {
  return tokens;
}

const NAME_RE = /^[a-z][a-z0-9-]*$/;
// A value may not end the declaration or the block it sits in.
const UNSAFE_VALUE_RE = /[;{}<]|\/\*/;

function declarations(map: TokenMap | undefined, indent: string): string {
  if (!map) return '';
  return Object.entries(map)
    .map(([name, value]) => {
      if (!NAME_RE.test(name)) throw new Error(`skin token "${name}" is not a valid name`);
      if (UNSAFE_VALUE_RE.test(value)) {
        throw new Error(`skin token "${name}" has an unsafe value: ${value}`);
      }
      return `${indent}--${name}: ${value};`;
    })
    .join('\n');
}

function rule(selector: string, map: TokenMap | undefined): string {
  const body = declarations(map, '  ');
  return body ? `${selector} {\n${body}\n}\n` : '';
}

/**
 * The stylesheet for one skin. Selectors repeat the attribute so they
 * out-rank theme.css (`:root`, `[data-theme='light']`, density blocks)
 * whatever order the sheets load in, and they work on <html> and on a
 * preview wrapper alike.
 */
export function skinTokensCss(id: SkinId, tokens: SkinTokens): string {
  const self = `[${SKIN_ATTR}='${id}'][${SKIN_ATTR}]`;
  const roles = tokens.roles ?? {};
  const prefix = SKIN_ROLE_PREFIX[id];
  for (const name of Object.keys({ ...roles.base, ...roles.light, ...roles.dark })) {
    if (!name.startsWith(prefix)) {
      throw new Error(`skin role "${name}" must start with "${prefix}"`);
    }
  }
  const merge = (a: TokenMap | undefined, b: TokenMap | undefined): TokenMap | undefined =>
    a || b ? { ...a, ...b } : undefined;
  // Velora Modern is the house look: sections that pin their own light or
  // dark inside it (the sign-in hero, statement print sheets) take its
  // colours too, not theme.css's. The Familiar modes keep theme.css there.
  const nested = (mode: 'light' | 'dark') =>
    id === 'velora' ? `,\n${self} [data-theme='${mode}']:not([${SKIN_ATTR}])` : '';
  const out = [
    `/* skin "${id}" (generated at runtime from @velora/tokens/skins/${id}) */\n`,
    rule(self, merge(tokens.base, roles.base)),
    rule(`${self}[data-theme='light']${nested('light')}`, merge(tokens.light, roles.light)),
    rule(
      `${self}[data-theme='dark'],\n${self}:not([data-theme])${nested('dark')}`,
      merge(tokens.dark, roles.dark),
    ),
  ];
  for (const [density, map] of Object.entries(tokens.density ?? {})) {
    out.push(rule(`${self}[data-density='${density}']`, map));
  }
  out.push(rule(`${self} .vel-shell__nav,\n${self} .vel-shell__top`, tokens.chrome));
  return out.filter(Boolean).join('\n');
}

/** Every role name a skin sets (`roles`), for the prefix check. */
export function skinRoleNames(tokens: SkinTokens): string[] {
  const r = tokens.roles ?? {};
  return [...new Set([r.base, r.light, r.dark].flatMap((m) => Object.keys(m ?? {})))].sort();
}

/** Every token name a skin sets (roles excluded), for the known-name check. */
export function skinTokenNames(tokens: SkinTokens): string[] {
  const maps = [
    tokens.base,
    tokens.light,
    tokens.dark,
    tokens.chrome,
    ...Object.values(tokens.density ?? {}),
  ];
  return [...new Set(maps.flatMap((m) => Object.keys(m ?? {})))].sort();
}
